import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  forkJoin,
  merge,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { DrillDownComponent } from '../../../common/drilldown/drilldown.component';
import { IDataProvider } from '../../../common/treeview/treeview.component';
import {
  AssignBusinessUnitAdministrator,
  AssignmentBusinessUnitInfo,
} from '../../../models/identity';
import { TreeViewEntity } from '../../../models/tree-view-entity.model';
import { BusinessUnitService } from '../../../services/business-unit.service';
import { ConfigService } from '../../../services/config.service';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-assign-hr-for-business-unit',
  templateUrl: './assign-hr-for-business-unit.component.html',
  styleUrls: ['./assign-hr-for-business-unit.component.scss'],
})
export class AssignHrForBusinessUnitComponent implements OnInit, OnDestroy {
  @ViewChild('drilldownSelect') drilldownSelect: DrillDownComponent;
  businessUnitSubject = new BehaviorSubject<Array<TreeViewEntity>>(null);
  adminsFilterOptions: Array<AssignBusinessUnitAdministrator> = [];
  selectedBUAdmins: Array<AssignBusinessUnitAdministrator> = [];
  baseBUAdmins: Array<AssignBusinessUnitAdministrator> = [];
  anotherBUs: Array<TreeViewEntity> = [];
  isAdditionalBusinessUnitsRequired = false;
  isSpinnerEnable = false;
  isLoading = false;
  canSave = false;
  selectedBU: TreeViewEntity;
  saveSubs: Subscription;
  businessUnitSubs: Subscription;
  businessUnitAdminsSubs: Subscription;
  businessUnitDataProvider: IDataProvider;
  allBusinessUnits: Array<AssignmentBusinessUnitInfo>;
  employeeSearcheSubject = new Subject<string>();
  employeeSearcheSubs: Subscription;

  withoutAdmins = new FormControl(false);

  constructor(
    private _businessUnitService: BusinessUnitService,
    private _configService: ConfigService,
    private _userService: UserService
  ) {}

  ngOnInit() {
    this.enabledLoadingSpiner();
    this.businessUnitSubs = forkJoin(
      this._businessUnitService.getTreeView(),
      this._businessUnitService.getAllBusinessUnitInfo()
    ).subscribe(
      ([businessUnitsTree, businessUnitsInfo]) => {
        this.businessUnitSubject.next(businessUnitsTree);
        this.allBusinessUnits = businessUnitsInfo;
        this.disabledLoadingSpiner();
      },
      () => {
        this.disabledLoadingSpiner();
      }
    );
    this.businessUnitDataProvider = {
      getKey: (obj) => obj.id,
      getDisplayName: (obj) => obj.name,
      loadData: this.searchBU(),
    };

    this.employeeSearcheSubs = this.employeeSearcheSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((event) => this._userService.getBySearchPattern(event))
      )
      .subscribe((data) => {
        this.adminsFilterOptions = data;
      });
  }

  ngOnDestroy(): void {
    if (this.businessUnitSubs) {
      this.businessUnitSubs.unsubscribe();
    }
    if (this.saveSubs) {
      this.saveSubs.unsubscribe();
    }
    if (this.businessUnitAdminsSubs) {
      this.businessUnitAdminsSubs.unsubscribe();
    }
    if (this.businessUnitSubject) {
      this.businessUnitSubject.complete();
    }
    if (this.employeeSearcheSubs) {
      this.employeeSearcheSubs.unsubscribe();
    }
    if (this.employeeSearcheSubject) {
      this.employeeSearcheSubject.complete();
    }
  }

  searchBU(): (search: string) => Observable<Array<TreeViewEntity>> {
    return (search: string = null) => {
      return merge(
        this.businessUnitSubject,
        this.withoutAdmins.valueChanges
      ).pipe(
        switchMap((x) => this.businessUnitSubject),
        map((x) => this.filterBusinessUnits(x, search))
      );
    };
  }

  filterBusinessUnits(
    busiessUnits: Array<TreeViewEntity>,
    search: string,
    parentHasSearchText: boolean = false
  ): Array<TreeViewEntity> {
    if (!search && !this.withoutAdmins.value) {
      return busiessUnits;
    }
    return busiessUnits
      .map((x) => {
        let busiessUnit = { ...x };
        const hasSearchText =
          parentHasSearchText ||
          this.businessUnitTextFilter(busiessUnit, search);
        const isWithoutHR = this.businessUnitHRFilter(busiessUnit);
        const children = this.filterBusinessUnits(
          busiessUnit.children,
          search,
          hasSearchText
        );
        const hasChildren = children && children.length;
        if ((hasSearchText && isWithoutHR) || hasChildren) {
          busiessUnit.children = children;
          return busiessUnit;
        }
        return null;
      })
      .filter((busiessUnit) => busiessUnit);
  }

  businessUnitTextFilter = (busiessUnit: TreeViewEntity, search: string) => {
    if (search) {
      return busiessUnit.name.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  };

  businessUnitHRFilter = (busiessUnit: TreeViewEntity) => {
    if (this.withoutAdmins.value) {
      const hrs = this.allBusinessUnits.find(
        (x) => x.id == busiessUnit.id
      ).businessUnitAdministrators;
      return !hrs || !hrs.length;
    }
    return true;
  };

  selectBU(selectedItem) {
    this.enabledSpiner();
    this.baseBUAdmins = [];
    this.cancelChanges();
    this.selectedBU = selectedItem;
    this.businessUnitAdminsSubs = this._businessUnitService
      .getAssignedToBusinessUnitBusinessUnitAdministrators(this.selectedBU.id)
      .pipe(
        catchError(() => {
          return [];
        })
      )
      .subscribe((data) => {
        this.disabledSpiner();
        this.selectedBUAdmins = data;
        this.baseBUAdmins = [...data];
      });
  }

  deleteSelectedAdmin(buAdminId: number) {
    const index = this.selectedBUAdmins.findIndex(
      (buAdmin) => buAdmin.id === buAdminId
    );
    if (index > -1) {
      this.selectedBUAdmins.splice(index, 1);
      this.canSave = true;
    }
  }

  searchBusinessUnitAdministratorByName(event: any) {
    if (!this.checkIfPatternReady(event)) {
      return;
    }
    this.employeeSearcheSubject.next(event);
  }

  assignBusinessUnitAdministrator(buAdminId: number) {
    const buAdmin = buAdminId
      ? this.adminsFilterOptions.find((buAdmin) => buAdmin.id === buAdminId)
      : null;
    if (
      buAdmin &&
      !this.selectedBUAdmins.some((buAdmin) => buAdmin.id === buAdminId)
    ) {
      this.selectedBUAdmins.push(buAdmin);
      this.canSave = true;
    }
  }

  unAssignBusinessUnitAdministrator(buAdminId: number) {
    this.selectedBUAdmins = this.selectedBUAdmins.filter(
      (buAdmin) => buAdmin.id !== buAdminId
    );
  }

  deleteSelectedBU(buId: number) {
    const index = this.anotherBUs.findIndex((bu) => bu.id === buId);
    if (index > -1) {
      this.anotherBUs.splice(index, 1);
      this.canSave = true;
    }
  }

  showAdditionalBusinessUnits() {
    this.isAdditionalBusinessUnitsRequired = true;
  }

  onBusinessUnitSelected(item: TreeViewEntity) {
    if (
      item.id &&
      !this.anotherBUs.find((x) => x.id === item.id) &&
      this.selectedBU.id !== item.id
    ) {
      this.canSave = true;
      this.anotherBUs.push(item);
      if (this.drilldownSelect) {
        this.drilldownSelect.onClearClicked();
      }
    }
  }

  saveChanges() {
    this.enabledLoadingSpiner();
    this.saveSubs = this._businessUnitService
      .updateBussinesUnitAdministrators(
        this.selectedBU.id,
        this.anotherBUs.map((x) => x.id),
        this.selectedBUAdmins
      )
      .pipe(tap(() => this.updateSelectedBusinessUnitsInfo()))
      .subscribe(() => {
        this.disabledLoadingSpiner();
        this.baseBUAdmins = [...this.selectedBUAdmins];
        this.cancelChanges();
      });
  }

  cancelChanges() {
    this.anotherBUs = [];
    this.selectedBUAdmins = [...this.baseBUAdmins];
    this.isAdditionalBusinessUnitsRequired = false;
    this.canSave = false;
  }

  private getRemovedBusinessUnitAdministrators() {
    return this.baseBUAdmins.filter(
      (x) => !this.selectedBUAdmins.find((z) => z.id == x.id)
    );
  }

  private updateSelectedBusinessUnitsInfo() {
    const selectedBusinessUnits = [this.selectedBU, ...this.anotherBUs];
    const removedBusinessUnitAdministrators =
      this.getRemovedBusinessUnitAdministrators();
    selectedBusinessUnits.forEach((selectedBusinessUnit) => {
      this.updateSelectedBusinessUnitInfo(
        selectedBusinessUnit,
        removedBusinessUnitAdministrators
      );
    });
  }

  private updateSelectedBusinessUnitInfo(
    selectedBusinessUnit: TreeViewEntity,
    removedBusinessUnitAdministrators: Array<AssignBusinessUnitAdministrator>
  ) {
    let targetBU = this.allBusinessUnits.find(
      (x) => x.id == selectedBusinessUnit.id
    );

    targetBU.businessUnitAdministrators =
      targetBU.businessUnitAdministrators.filter(
        (x) => !removedBusinessUnitAdministrators.find((z) => z.id == x.id)
      );

    const newBusinessUnitAdministrators = this.selectedBUAdmins.filter(
      (x) => !targetBU.businessUnitAdministrators.find((z) => z.id == x.id)
    );
    targetBU.businessUnitAdministrators.push(...newBusinessUnitAdministrators);

    selectedBusinessUnit.children.forEach((child) => {
      this.updateSelectedBusinessUnitInfo(
        child,
        removedBusinessUnitAdministrators
      );
    });
  }

  adminsExists(key: number | string) {
    const businessUnit = this.allBusinessUnits
      ? this.allBusinessUnits.find((x) => x.id == key)
      : null;
    return (
      businessUnit &&
      businessUnit.businessUnitAdministrators &&
      businessUnit.businessUnitAdministrators.length
    );
  }

  private enabledSpiner() {
    this.isSpinnerEnable = true;
  }

  private disabledSpiner() {
    this.isSpinnerEnable = false;
  }

  private enabledLoadingSpiner() {
    this.enabledSpiner();
    this.isLoading = true;
  }

  private disabledLoadingSpiner() {
    this.isLoading = false;
    this.disabledSpiner();
  }

  private checkIfPatternReady(pattern: string): boolean {
    return (
      pattern.length >= this._configService.configData.minimumSearchStringLength
    );
  }
}
